import React, { FC, memo } from 'react';

import { TEXTS } from 'application/pages/BillingPage/renderer/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useOnboardingActionContext, useOnboardingContext } from 'application/components/common';

import Button from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { useCurrentUserSberConnection } from '@entities/auth';

import useLicensesModal from '../../model/useLicensesModal';
import LicensesList from '../LicensesList/LicensesList';
import LicensesUnpaidList from '../LicensesUnpaidList/LicensesUnpaidList';
import LicensesModalActions from '../LicensesModalActions/LicensesModalActions';
import LicensesModalHint from '../LicensesModalHint/LicensesModalHint';

import styles from './LicensesModal.module.scss';

const LicensesModal: FC<ModalProps[ModalTypes.LICENSES]> = ({
  close,
  payFrequencyMonth = 1,
  unpaidLicensesOnly = false,
  isViewMode = false,
}) => {
  const { currentPoint } = useOnboardingContext();
  const { setCurrentPoint, clearCurrentPoint } = useOnboardingActionContext();

  const hasSberConnection = useCurrentUserSberConnection();

  const licensesModal = useLicensesModal({
    unpaidLicensesOnly,
    isViewMode,
    clearCurrentPoint,
    close,
  });

  const closeModalButton = (
    <Button
      key="button_confirm_autorenewal"
      elementId="button_confirm_autorenewal"
      onClick={() => close(2)}>
      {TEXTS.BILLING_AUTORENEWAL_SUSPENDED_SUBMIT}
    </Button>
  );

  return (
    <ModalScreen
      title={
        licensesModal?.hasInactiveLicenses
          ? TEXTS.BILLING_LICENSES_RESUME_MODAL_TITLE(payFrequencyMonth === 1)
          : TEXTS.BILLING_LICENSES_MODAL_TITLE
      }
      titlePlacement="left"
      onClose={licensesModal?.handleLicensesModalClose}
      containerClassName={styles.licensesModalContainer}
      contentClassName={styles.licensesModalContent}
      containerActionButtonsClassName={styles.licensesModalActionButtons}
      actionButtons={
        licensesModal && (
          <LicensesModalActions
            viewMode={licensesModal.viewMode}
            bindCardVisible={licensesModal.bindCardVisible}
            isSubmitLoading={licensesModal.isSubmitLoading}
            isPendingAcceptanceAdvanced={licensesModal.isPendingAcceptanceAdvanced}
            hasSberConnection={hasSberConnection}
            isChangeAutoRenewalButtonDisabled={licensesModal.isChangeAutoRenewalButtonDisabled}
            licensesSubscriptionCost={licensesModal.state.licensesSubscriptionCost}
            isLinkingInProcess={licensesModal.isLinkingInProcess}
            isHintShow={licensesModal.isHintShow}
            isNeedPayment={licensesModal.isNeedPayment}
            close={close}
            state={licensesModal.state}
            dispatch={licensesModal.dispatch}
            currentPoint={currentPoint}
            setCurrentPoint={setCurrentPoint}
            closeModalButton={closeModalButton}
            unpaidLicensesOnly={unpaidLicensesOnly}
            hasTrialLicenses={licensesModal.hasTrialLicenses}
            hasInactiveLicenses={licensesModal.hasInactiveLicenses}
            hasWaitPaymentLicenses={licensesModal.hasWaitPaymentLicenses}
            hasGraceLicenses={licensesModal.hasGraceLicenses}
            paymentMethod={licensesModal.paymentMethod}
            selectedPaymentKind={licensesModal.selectedPaymentKind}
            acceptanceAdvanceStatus={licensesModal.acceptanceAdvanceStatus}
            toEnableAutoRenewalPaid={licensesModal.toEnableAutoRenewalPaid}
            toEnableAutoRenewalUnpaid={licensesModal.toEnableAutoRenewalUnpaid}
            suspendAutoRenewal={licensesModal.suspendAutoRenewal}
            resumeAutoRenewal={licensesModal.resumeAutoRenewal}
            refetchBillingInfo={licensesModal.refetchBillingInfo}
            getLinkForBindingCard={licensesModal.getLinkForBindingCard}
            getLinkForPayment={licensesModal.getLinkForPayment}
          />
        )
      }>
      {licensesModal ? (
        <>
          {unpaidLicensesOnly ? (
            <LicensesUnpaidList
              unpaidLicenses={licensesModal.unpaidLicenses}
              toEnableAutorenewal={licensesModal.state.toEnableAutorenewal}
              dispatch={licensesModal.dispatch}
              setSubscriptionCost={licensesModal.setSubscriptionCost}
            />
          ) : (
            <LicensesList
              totalLicenses={licensesModal.totalLicenses}
              toSuspendAutorenewal={licensesModal.state.toSuspendAutorenewal}
              toEnableAutorenewal={licensesModal.state.toEnableAutorenewal}
              dispatch={licensesModal.dispatch}
              setSubscriptionCost={licensesModal.setSubscriptionCost}
              isAllLicensesDisabled={licensesModal.viewMode}
            />
          )}

          <LicensesModalHint
            isOneLicense={payFrequencyMonth === 1}
            isPendingAcceptanceAdvanced={licensesModal.isPendingAcceptanceAdvanced}
            hasSberConnection={hasSberConnection}
            withResidualPayment={licensesModal.withResidualPayment}
            licensesSubscriptionCost={licensesModal.state.licensesSubscriptionCost}
            formatNextBillingDate={licensesModal.formatNextBillingDate}
          />
        </>
      ) : (
        <div className={styles.licensesSkeleton} />
      )}
    </ModalScreen>
  );
};

export default memo(LicensesModal);
